import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import * as XLSX from "xlsx";

export default function ImportFacturi() {
  const [direction, setDirection] = useState("furnizori"); // 'furnizori' or 'clienti'
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  const clearFile = () => {
    setFile(null);
    setFileName("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const changeDirection = (value) => {
    setDirection(value);
    clearFile();
    setError("");
    setSuccess("");
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await authFetch("/accounts?limit=1000&sort_by=code");
      if (res && res.items) {
        setAccounts(res.items);
      }
    } catch (err) {
      console.error("Eroare la incarcarea planului de conturi:", err);
    }
  };

  // Filter accounts based on direction
  const filteredAccounts = accounts.filter(acc => {
    const target = direction === "furnizori" ? 1 : 11;
    const configured = Number(acc.bxswitch || 0);
    const prefixes = target === 1 ? ["167", "401", "404", "408"] : ["411", "418"];
    return acc.isactive !== false && [0, target].includes(configured) &&
      (configured === target || prefixes.some(prefix => String(acc.code || "").trim().startsWith(prefix)));
  });

  // Automatically select the first account when filtered list changes
  useEffect(() => {
    if (filteredAccounts.length > 0) {
      setSelectedAccountId(filteredAccounts[0].id);
    } else {
      setSelectedAccountId("");
    }
  }, [direction, accounts]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError("");
      setSuccess("");
    }
  };

  const downloadTemplate = () => {
    const headers = [
      [
        "NUMAR_FACTURA",
        "DATA_FACTURA",
        "DATA_SCADENTA",
        direction === "clienti" ? "CUI_CLIENT" : "CUI_FURNIZOR",
        direction === "clienti" ? "NUME_CLIENT" : "NUME_FURNIZOR",
        "TVA_LA_INCASARE",
        "COTA_TVA",
        "VALOARE_NETA",
        "VALOARE_TVA",
        "VALOARE_TOTALA",
        "MONEDA",
        "CURS_VALUTAR",
        "SOLD_FACTURA",
        "CONT_CONTABIL"
      ],
      [
        "FAC-001",
        "2025-01-15",
        "2025-02-15",
        "RO12345678",
        direction === "clienti" ? "Client ABC SRL" : "Furnizor ABC SRL",
        direction === "clienti" ? "" : 1,
        19.0,
        1000.00,
        190.00,
        1190.00,
        "LEI",
        1.0,
        1190.00,
        ""
      ]
    ];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    ws["!cols"] = headers[0].map(label => ({ wch: Math.max(label.length + 2, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturi");
    XLSX.writeFile(wb, `model_import_facturi_${direction}.xlsx`);
  };

  const handleUpload = () => {
    if (!file) {
      setError("Te rog selecteaza un fisier Excel mai intai.");
      return;
    }
    if (!selectedAccountId) {
      setError("Te rog selecteaza un cont contabil.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws);
        const columnNames = (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || [])
          .map(key => String(key).toLowerCase().trim().replace(/[\s\-_.]+/g, "_"));
        const oppositeColumn = direction === "clienti" ? "cui_furnizor" : "cui_client";
        if (columnNames.includes(oppositeColumn)) {
          throw new Error(`Fișierul nu corespunde importului de ${direction}. Descarcă modelul pentru tipul selectat.`);
        }

        // Normalize keys and filter
        const cleanedData = rawData.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            const normKey = key.toLowerCase().trim().replace(/[\s\-_.]+/g, "_");
            newRow[normKey] = row[key];
          });
          return newRow;
        });
        cleanedData.forEach((row, index) => {
          const codFiscal = row.cui_furnizor || row.cui_client || row.cod_fiscal || row.cui || row.cif || row.cui_partener || row.fiscal_code || row.codfiscal;
          const numarFactura = row.numar_factura || row.numar || row.nr_factura || row.document || row.doc_no || row.factura;
          if (!codFiscal || !numarFactura) {
            throw new Error(`Înregistrarea ${index + 1}: completează CUI și NUMAR_FACTURA. Nu s-a importat niciun rând.`);
          }
        });

        if (cleanedData.length === 0) {
          throw new Error("Fisierul Excel nu contine date valide. Asigura-te ca exista coloanele CUI_PARTENER (sau COD_FISCAL) si NUMAR_FACTURA.");
        }

        const res = await authFetch("/imports/facturi-initiale", {
          method: "POST",
          body: JSON.stringify({
            data: cleanedData,
            contID: selectedAccountId,
            bxswitch: direction === "furnizori" ? 1 : 11
          })
        });

        if (res.result === "pass") {
          setSummary(res.summary || null);
          window.dispatchEvent(new Event("accounts-menu-changed"));
          setSuccess(`Facturile inițiale de ${direction} au fost importate cu succes!`);
          clearFile();
        } else {
          setError(res.errorMsg || "Importul a esuat.");
        }
      } catch (err) {
        setError(err.message || "Eroare la procesarea fisierului Excel.");
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Fișierul nu a putut fi citit. Selectează-l din nou.");
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4 bg-white">
      <div className="bg-gradient bg-primary p-4 text-white">
        <h3 className="mb-1 fw-bold">Import Facturi Inițiale</h3>
        <p className="mb-0 text-white-50 small">Preluare solduri facturi de la clienți sau furnizori.</p>
      </div>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
        {success && <Alert variant="success" className="rounded-3">
          <div className="fw-bold">{success}</div>
          {summary && <>
            <p className="mt-2 mb-2">
              Rânduri procesate: <strong>{summary.processed}</strong>. Facturi noi: <strong>{summary.created}</strong>.
              {" "}Actualizări ale facturilor existente: <strong>{summary.updated}</strong>.
            </p>
            {summary.accounts.map(account => <div key={account.account_id} className="border-top pt-2 mt-2">
              <div><strong>Cont {account.code}</strong> — {account.name}</div>
              <div>Facturi noi: {account.created}; actualizări: {account.updated}.</div>
              <div>Le găsești în <strong>{account.menu_section} → {account.menu_label}</strong>.</div>
              <div>{account.menu_added ? "Contul a fost adăugat în meniu." : "Contul era deja disponibil în meniu."}</div>
              <Button as="a" href={account.path} variant="outline-success" size="sm" className="mt-2">
                Vezi facturile — {account.code}
              </Button>
            </div>)}
            <p className="small mt-3 mb-0">Contul din fiecare rând Excel are prioritate. Dacă lipsește, se folosește contul selectat la import. Butoanele deschid toate facturile contului respectiv.</p>
          </>}
        </Alert>}

        <Form className="mb-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-dark mb-2">Tip parteneri</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="radio-furnizori"
                    label="Import furnizori (167, 401, 404, 408)"
                    name="direction"
                    checked={direction === "furnizori"}
                    onChange={() => changeDirection("furnizori")}
                    disabled={loading}
                    className="fw-semibold text-secondary"
                  />
                  <Form.Check
                    type="radio"
                    id="radio-clienti"
                    label="Import clienți (411, 418)"
                    name="direction"
                    checked={direction === "clienti"}
                    onChange={() => changeDirection("clienti")}
                    disabled={loading}
                    className="fw-semibold text-secondary"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold text-dark mb-2">Cont implicit (pentru rândurile fără CONT_CONTABIL)</Form.Label>
                <Form.Select
                  id="select-account"
                  disabled={loading}
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  className="rounded-3"
                >
                  {filteredAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.code} - {acc.name}
                    </option>
                  ))}
                  {filteredAccounts.length === 0 && (
                    <option value="">Nu s-au găsit conturi corespunzătoare</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-1">Model Excel</h5>
            <p className="text-muted small mb-0">Descarcă modelul adaptat tipului de import selectat.</p>
          </div>
          <Button variant="outline-primary" onClick={downloadTemplate} className="rounded-pill px-4">
            Descarcă model Excel ({direction})
          </Button>
        </div>

        <hr className="my-4 text-black-50" />

        <Alert variant="info">
          <strong>TVA_LA_INCASARE: </strong>
          {direction === "clienti"
            ? "Completează 1/DA pentru o factură emisă cu TVA la încasare sau 0/NU pentru una fără. Câmpul gol preia setarea firmei tale. Valoarea se referă la factura emisă, nu la regimul fiscal al clientului."
            : "Completează 1/DA dacă factura furnizorului este cu TVA la încasare, altfel 0/NU. Dacă firma ta are TVA la încasare, importul activează acest regim și pentru facturile furnizorilor."}
          <div className="mt-2">CONT_CONTABIL este opțional: dacă rămâne gol, se folosește contul selectat mai sus.</div>
        </Alert>

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold text-dark mb-2">Selectare fișier import</Form.Label>
          <div className="border border-2 border-dashed border-primary rounded-3 p-4 text-center cursor-pointer position-relative">
            <input
              type="file"
              ref={fileInput}
              disabled={loading}
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
            />
            <div className="py-2">
              <i className="bi bi-file-earmark-excel text-primary display-4 mb-2"></i>
              <p className="fw-semibold text-secondary mb-1">
                {fileName || "Trage fișierul aici sau fă click pentru a-l selecta"}
              </p>
              <p className="text-muted small mb-0">Sunt acceptate doar fișiere Excel (.xlsx, .xls)</p>
            </div>
          </div>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={loading || !file || !selectedAccountId}
            className="rounded-pill px-4 py-2 fw-semibold d-flex align-items-center"
          >
            {loading && <Spinner size="sm" animation="border" className="me-2" />}
            {loading ? "Se importă..." : "Efectuează Import"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
